import React, { useState, useRef } from 'react';
import api from '../../services/api';
import {
  FileText, X
} from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { useAuth } from '../../contexts/AuthContext';
const UploadInventory = () => {
  // Upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Handle file input change
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    await processFiles(files);
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  };

  // Process uploaded files
  const processFiles = async (files) => {
    setIsProcessing(true);
    setProgress(0);
    let allProcessedItems = [];
    let errors = {};
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        // Send file to backend for batch processing
        const response = await api.uploadJewelryFile(file);
        if (response && response.batch_id) {
          const batchItems = await api.getBatchItems(response.batch_id);
          const itemsData = batchItems?.data || [];
          allProcessedItems.push(...itemsData.map(item => ({
            ...calculateItemFields(item),
            fileName: file.name,
            fileIndex: i,
            batchId: response.batch_id,
            uploadId: item.id
          })));
          setUploadedFiles(prev => [...prev, {
            name: file.name,
            size: file.size,
            itemCount: itemsData.length,
            status: 'processed',
            batchId: response.batch_id
          }]);
        } else {
          throw new Error(response.message || `Upload failed for ${file.name}`);
        }
      } catch (error) {
        errors[file.name] = [`Failed to process file: ${error.message}`];
        setUploadedFiles(prev => [...prev, {
          name: file.name,
          size: file.size,
          itemCount: 0,
          status: 'error',
          error: error.message
        }]);
      }
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setProcessedItems(allProcessedItems);
    setValidationErrors(errors);
    setIsProcessing(false);
    if (allProcessedItems.length > 0 && Object.keys(errors).length === 0) {
      setApprovedItems(allProcessedItems.map((_, index) => index));
    }
  };
  // Column mapping for auto-import
  // eslint-disable-next-line no-unused-vars
  const defaultMappings = {
    itemCode: ['Item Code', 'Code', 'SKU'],
    name: ['Description', 'Name', 'Item Name'],
    category: ['Category', 'Type'],
    goldWeight: ['Gold Weight', 'GoldWt', 'Gold (g)'],
    goldPurity: ['Gold Purity', 'Purity', 'Karat'],
    goldRate: ['Gold Rate', 'Gold Price'],
    diamondWeight: ['Diamond Weight', 'DiamondWt', 'Diamond (ct)'],
    diamondRate: ['Diamond Rate', 'Diamond Price'],
    stoneWeight: ['Stone Weight', 'StoneWt', 'Stone (ct)'],
    stoneRate: ['Stone Rate', 'Stone Price'],
    laborCost: ['Labor Cost', 'Making Charges'],
    otherCosts: ['Other Costs', 'Miscellaneous'],
    salePrice: ['Sale Price', 'Selling Price'],
    vendor: ['Vendor', 'Supplier'],
    notes: ['Notes', 'Remarks']
  };

  // Helper to recalculate item fields
  function calculateItemFields(item) {
    // Defensive copy
    const newItem = { ...item };
    const purityMultiplier = newItem.goldPurity / 24;
    newItem.goldAmount = newItem.goldWeight * newItem.goldRate * purityMultiplier;
    newItem.diamondAmount = newItem.diamondWeight * newItem.diamondRate;
    newItem.stoneAmount = newItem.stoneWeight * newItem.stoneRate;
    newItem.totalCost = newItem.goldAmount + newItem.diamondAmount + newItem.stoneAmount + newItem.laborCost + newItem.otherCosts;
    if (!newItem.salePrice || newItem.salePrice === 0) {
      newItem.salePrice = newItem.totalCost * 1.35;
    }
    newItem.profit = newItem.salePrice - newItem.totalCost;
    newItem.profitPercentage = newItem.totalCost > 0 ? (newItem.profit / newItem.totalCost * 100) : 0;
    return newItem;
  }
  // Auth context
  const { isAuthenticated, user } = useAuth();
  // State management
  const [processedItems, setProcessedItems] = useState([]);
  const [approvedItems, setApprovedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [uploadHistory, setUploadHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const fileInputRef = useRef(null);

  const viewBatchItems = (batchId) => {
    setSelectedBatchId(prev => prev === batchId ? null : batchId);
  };


  // Process individual file
  // const processFile = async (file) => {
  //   const fileType = file.name.split('.').pop().toLowerCase();
  //   
  //   if (fileType === 'csv') {
  //     return await readCSVFile(file);
  //   } else if (fileType === 'xlsx' || fileType === 'xls') {
  //     return await readExcelFile(file);
  //   } else {
  //     throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  //   }
  // };





  // Validate item
  // eslint-disable-next-line no-unused-vars
  const validateItem = (item) => {
    const errors = [];

    // Required fields
    if (!item.itemCode || item.itemCode.trim() === '') {
      errors.push('Item Code is required');
    }
    if (!item.name || item.name.trim() === '') {
      errors.push('Item Name is required');
    }
    if (!item.category || item.category.trim() === '') {
      errors.push('Category is required');
    }

    // Validate numeric fields
    if (item.goldWeight < 0) errors.push('Gold Weight cannot be negative');
    if (item.goldPurity < 0 || item.goldPurity > 24) {
      errors.push('Gold Purity must be between 0 and 24');
    }
    if (item.totalCost < 0) errors.push('Total Cost cannot be negative');
    if (item.salePrice < 0) errors.push('Sale Price cannot be negative');

    // Check for duplicate item codes
    const existingItems = JSON.parse(localStorage.getItem('jewelry') || '[]');
    if (existingItems.some(existing => existing.code === item.itemCode)) {
      errors.push(`Duplicate Item Code: ${item.itemCode} already exists`);
    }

    return errors;
  };

  // Reset upload state
  // eslint-disable-next-line no-unused-vars
  const resetUpload = () => {
    setProcessedItems([]);
    setApprovedItems([]);
    setUploadedFiles([]);
    setValidationErrors({});
    setProgress(0);
    setShowHistory(false);
  };

  // Load upload history from backend
  // eslint-disable-next-line no-unused-vars
  const loadUploadHistory = async () => {
    if (!isAuthenticated || !user) {
      alert('Please log in to view upload history.');
      return;
    }
    try {
      const batches = await api.getUploadBatches();
      setUploadHistory(batches || []);
      setShowHistory(true);
    } catch (error) {
      setUploadHistory([]);
      setShowHistory(true);
      alert('Failed to load upload history. Please try again.');
    }
  };


  return (
    <div>
      <PageIdentifier pageId={SCREEN_IDS.UPLOAD.UPLOAD_INVENTORY} pageName="Upload Inventory" />
      {/* Upload UI */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept=".csv,.xlsx,.xls"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing}
        >
          Select Files to Upload
        </button>
        <p className="mt-2 text-gray-600">or drag and drop CSV/Excel files here</p>
        {isProcessing && (
          <div className="mt-4">
            <p className="text-blue-600">Uploading... {progress}%</p>
          </div>
        )}
      </div>

      {/* Uploaded Files Status */}
      {uploadedFiles.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Uploaded Files</h3>
          <ul className="space-y-2">
            {uploadedFiles.map((file, idx) => (
              <li key={idx} className={`p-3 rounded border ${file.status === 'error' ? 'border-red-400 bg-red-50' : 'border-green-400 bg-green-50'}`}>
                <span className="font-medium">{file.name}</span> — {file.itemCount} items {file.status === 'error' && <span className="text-red-600">(Error: {file.error})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Item Review Table */}
      {processedItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <h3 className="text-lg font-semibold px-6 py-4 border-b">Review Uploaded Items</h3>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Item Code</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-right">Total Cost</th>
                <th className="px-4 py-3 text-right">Sale Price</th>
                <th className="px-4 py-3 text-right">Profit %</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {processedItems.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-3">{item.itemCode}</td>
                  <td className="px-4 py-3">{item.name}</td>
                  <td className="px-4 py-3">{item.category}</td>
                  <td className="px-4 py-3 text-right">${item.totalCost.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">${item.salePrice.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{item.profitPercentage.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-center">
                    {approvedItems.includes(idx) ? (
                      <span className="text-green-600 font-semibold">Approved</span>
                    ) : (
                      <span className="text-gray-600">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Error Display */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="mt-6 p-4 bg-red-100 border border-red-400 rounded">
          <h4 className="font-semibold text-red-700 mb-2">Upload Errors</h4>
          <ul className="list-disc list-inside text-red-600">
            {Object.entries(validationErrors).map(([file, errs]) => (
              <li key={file}><span className="font-bold">{file}:</span> {errs.join(', ')}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Upload History Modal */}
      {showHistory && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Upload History</h3>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="p-6">
            {!uploadHistory || uploadHistory.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto mb-4 text-gray-300" size={48} />
                <p className="text-gray-600">No upload history found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {uploadHistory.map((batch) => (
                  <div key={batch.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold">{batch.filename}</h4>
                        <p className="text-sm text-gray-600">
                          Uploaded {new Date(batch.created_at).toLocaleDateString()} at{' '}
                          {new Date(batch.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${batch.status === 'completed' ? 'bg-green-100 text-green-800' :
                        batch.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                        {batch.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                      <span>Total Items: {batch.total_items}</span>
                      <span>Processed: {batch.processed_items || 0}</span>
                      <span>Approved: {batch.approved_items || 0}</span>
                    </div>
                    {batch.format && (
                      <p className="text-sm text-blue-600 mb-3">
                        Format: {batch.format}
                      </p>
                    )}
                    <button
                      onClick={() => viewBatchItems(batch.id)}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                    >
                      {selectedBatchId === batch.id ? 'Hide Items' : 'View Items'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );


}
export default UploadInventory;
